import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Clock, AlertTriangle } from 'lucide-react';

const ExamTimer = ({ 
  timeRemaining, 
  totalTime,
  onTimeUp,
  onExtendTime,
  allowExtension = false,
  showWarning = true
}) => {
  const [warningShown, setWarningShown] = React.useState(false);
  const [finalWarningShown, setFinalWarningShown] = React.useState(false);

  // Calculate percentage of time remaining
  const timePercentage = (timeRemaining / totalTime) * 100;
  
  // Format time display
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Get timer color based on remaining time
  const getTimerColor = () => {
    if (timePercentage <= 10) return 'text-red-600 bg-red-100';
    if (timePercentage <= 25) return 'text-orange-600 bg-orange-100';
    if (timePercentage <= 50) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const getProgressColor = () => {
    if (timePercentage <= 10) return 'bg-red-500';
    if (timePercentage <= 25) return 'bg-orange-500';
    if (timePercentage <= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Show warnings at specific time intervals
  React.useEffect(() => {
    if (timeRemaining <= 300 && timeRemaining > 0 && !warningShown && showWarning) { // 5 minutes
      setWarningShown(true);
      // You could trigger a notification here
    }
    
    if (timeRemaining <= 60 && timeRemaining > 0 && !finalWarningShown && showWarning) { // 1 minute
      setFinalWarningShown(true);
      // You could trigger a more urgent notification here
    }
    
    if (timeRemaining <= 0) {
      onTimeUp();
    }
  }, [timeRemaining, warningShown, finalWarningShown, showWarning, onTimeUp]);

  return (
    <Card className="w-full">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          {/* Timer Display */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Time Remaining:</span>
            </div>
            
            <Badge className={`text-lg font-mono px-3 py-1 ${getTimerColor()}`}>
              {formatTime(timeRemaining)}
            </Badge>
          </div>

          {/* Progress Bar */}
          <div className="flex-1 mx-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-1000 ${getProgressColor()}`}
                style={{ width: `${timePercentage}%` }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Extension Button */}
            {allowExtension && timeRemaining <= 300 && (
              <Button
                variant="outline"
                size="sm"
                onClick={onExtendTime}
                className="flex items-center gap-1 text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <AlertTriangle className="w-4 h-4" />
                Extend
              </Button>
            )}
          </div>
        </div>

        {/* Warning Messages */}
        {warningShown && timeRemaining <= 300 && timeRemaining > 60 && (
          <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-sm text-orange-800">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <span>Warning: Less than 5 minutes remaining!</span>
            </div>
          </div>
        )}

        {finalWarningShown && timeRemaining <= 60 && timeRemaining > 0 && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <span>Final Warning: Less than 1 minute remaining!</span>
            </div>
          </div>
        )}

        {/* Time Up Message */}
        {timeRemaining <= 0 && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <span>Time's up! Your exam will be submitted automatically.</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExamTimer;
